import _                                                     from 'underscore';
import classNames                                            from 'classnames';
import React, { FC, ReactNode }                              from 'react';
import { Checkbox as SUICheckbox, FormCheckboxProps, Popup } from 'semantic-ui-react';
import { FormikContextType, useField, useFormikContext }     from 'formik';
import { FieldWrapper }                                      from './FieldWrapper';
import { FieldDescription }                                  from './FieldDescription';
import { Flexbox, FlexItem, StyledContainer }                from '../../../containers';
import { FlexAlign, FlexJustify, Position }                  from '../../../../types/componentPropTypes';
import { SystemEvent }                                       from '../../../../types/events';
import { StandaloneFormikContextType }                       from '../../../../types/forms';
import { runIfFunction }                                     from '../../../../util/FunctionalUtility';


interface CheckboxProps extends FormCheckboxProps {
    label?: any,
    descriptionText?: ReactNode,
    includeFieldWrapper?: boolean
    includeErrorMessage?: boolean,
    indeterminateNegativeLabel?: string,
    indeterminatePositiveLabel?: string,
    expandLabelContainer?: boolean,
    noWrap?: boolean,
    noContent?: boolean,

    onLabelClick?( checked: boolean, value?: string | number | boolean ): void,

    onTransitionToDeterminate?( checked: boolean ): void,

    [ additionalProps: string ]: any
}

const defaultProps: CheckboxProps = {
    label:                      null,
    includeFieldWrapper:        true,
    includeErrorMessage:        true,
    indeterminateNegativeLabel: 'No',
    indeterminatePositiveLabel: 'Yes',
    expandLabelContainer:       false,
    noWrap:                     false,
    noContent:                  false,
    onLabelClick:               null,
    onTransitionToDeterminate:  null,
}

const Checkbox: FC<CheckboxProps> = ( _props ) => {

    let {
            label,
            descriptionText,
            includeFieldWrapper,
            includeErrorMessage,
            indeterminateNegativeLabel,
            indeterminatePositiveLabel,
            expandLabelContainer,
            noWrap,
            noContent,
            onLabelClick,
            onTransitionToDeterminate,
            ...props
        } = _props;

    // We need to tell useField what type of input this is
    // since React treats radios and checkboxes differently
    // than inputs/select/textarea.
    let formik: FormikContextType<any>              = useFormikContext(),
        controlProps: any                           = { ...props, type: 'checkbox' },
        standinContext: StandaloneFormikContextType = [ { name: props.name }, {}, {} ];

    const [ fieldProps, meta, helpers ] = formik ? useField( controlProps ) : standinContext;

    let { value, ...field } = fieldProps,
        showErrors          = ( meta.touched && meta.error ) as boolean,
        Wrapper             = includeFieldWrapper ? FieldWrapper : React.Fragment;

    let transitionToDeterminate = ( e: SystemEvent, nextState: boolean ) => {

        /* Update Formik */
        if( helpers.setValue ){

            e.preventDefault();

            helpers.setValue( nextState );
        }

        runIfFunction( onTransitionToDeterminate, nextState );
    }

    return (

        <Wrapper { ...{ showErrors, includeErrorMessage, meta, field } }>

            <Flexbox align={ noContent ? FlexAlign.CENTER : FlexAlign.START } justify={ noContent ? FlexJustify.CENTER : FlexJustify.START } noWrap={ noWrap }>

                <FlexItem noGrow className={ classNames( { 'margin-sm-right': !noContent }, 'position--relative' ) }>

                    { props.toggle && !( props.disabled ) && ( value === null ) && (
                        <>
                            <Popup content={ indeterminateNegativeLabel }
                                   position={ 'top center' }
                                   size={ 'mini' }
                                   trigger={
                                       <StyledContainer
                                           position={ Position.ABSOLUTE } top left bottom
                                           style={ { width: '50%' } }
                                           className={ 'cursor--pointer' }
                                           zIndex={ 100 }
                                           onClick={ _.partial( transitionToDeterminate, _, false ) }/>
                                   }/>

                            <Popup content={ indeterminatePositiveLabel }
                                   position={ 'top center' }
                                   size={ 'mini' }
                                   trigger={
                                       <StyledContainer
                                           position={ Position.ABSOLUTE } top right bottom
                                           style={ { width: '50%' } }
                                           className={ 'cursor--pointer' }
                                           zIndex={ 100 }
                                           onClick={ _.partial( transitionToDeterminate, _, true ) }/>
                                   }/>

                        </>
                    ) }

                    {/* Actual checkbox */ }
                    <SUICheckbox
                        { ...field }
                        { ...props }
                        key={ `${ props.name }-${ value ? ( value === true ) ? 'on' : 'off' : 'not-set' }` }
                        indeterminate={ _.isNull( value ) || _.isUndefined( value ) }
                    />

                </FlexItem>

                { !noContent && (
                    <FlexItem basis={ 50 } noGrow={ expandLabelContainer }>

                        <label className={ classNames( 'cursor--pointer', { 'text--no-wrap': noWrap, 'opacity--50': props.disabled } ) }
                               onClick={ () => {

                                   if( !props.disabled && helpers.setValue ){

                                       helpers.setValue( !value );

                                       runIfFunction( onLabelClick, !value, props.value ?? null );
                                   }

                               } }>{ label }</label>

                        { descriptionText && <FieldDescription>{ descriptionText }</FieldDescription> }

                    </FlexItem>
                ) }
            </Flexbox>

        </Wrapper>
    );
};

Checkbox.defaultProps = defaultProps;

export { Checkbox };
